import { useEffect, useRef } from "react";
import { cssVar, tg } from "../lib/tg";

/**
 * Системная нижняя кнопка Telegram (MainButton) как главное действие экрана.
 *
 * Зачем она вместо обычной кнопки в вёрстке: клиент рисует её поверх контента,
 * над клавиатурой и с системным индикатором загрузки — это тот элемент, который
 * пользователь ищет внизу в любом мини-аппе. Вне Telegram хук ничего не делает,
 * поэтому на сайте экран обязан оставаться рабочим и без неё.
 *
 * Цвета задаём своей палитрой: по умолчанию Telegram красит кнопку в акцент
 * своей темы, а у приложения тема собственная (оно же работает сайтом).
 */
export function useMainButton(opts: {
  text: string;
  onClick: () => void;
  visible?: boolean;
  /** Идёт запрос: системный спиннер, кнопка заблокирована. */
  progress?: boolean;
  enabled?: boolean;
}): void {
  const { text, visible = true, progress = false, enabled = true } = opts;

  // Обработчик держим в ref: onClick пересоздаётся на каждый рендер, а
  // подписываться/отписываться на каждый рендер — верный способ потерять клик.
  const cbRef = useRef(opts.onClick);
  cbRef.current = opts.onClick;

  useEffect(() => {
    const mb = tg()?.MainButton;
    if (!mb) return;
    const handler = () => cbRef.current();
    mb.onClick(handler);
    return () => {
      try {
        mb.offClick(handler);
        mb.hideProgress();
        mb.hide();   // уходя с экрана, кнопку за собой убираем
      } catch {
        /* старый клиент */
      }
    };
  }, []);

  useEffect(() => {
    const mb = tg()?.MainButton;
    if (!mb) return;
    try {
      mb.setParams({
        text,
        color: cssVar("--button") || undefined,
        text_color: cssVar("--button-text") || undefined,
      });
      // Прогресс выставляем раньше активности: showProgress(false) гасит
      // кнопку, и последующий enable() вернул бы её в кликабельное состояние
      // прямо во время запроса.
      if (progress) mb.showProgress(false);
      else mb.hideProgress();
      if (enabled && !progress) mb.enable();
      else mb.disable();
      if (visible) mb.show();
      else mb.hide();
    } catch {
      /* старый клиент */
    }
  }, [text, visible, progress, enabled]);
}

/**
 * Системная кнопка «назад» в шапке клиента.
 *
 * Показываем только на экранах, которых нет в нижней навигации (товар,
 * подписки, вложенные разделы): на самих вкладках стрелка «назад» в шапке
 * противоречит табам — уводит непонятно куда.
 */
export function useBackButton(onBack: () => void, visible = true): void {
  const cbRef = useRef(onBack);
  cbRef.current = onBack;

  useEffect(() => {
    const bb = tg()?.BackButton;
    if (!bb) return;
    const handler = () => cbRef.current();
    bb.onClick(handler);
    return () => {
      try {
        bb.offClick(handler);
        bb.hide();
      } catch {
        /* старый клиент */
      }
    };
  }, []);

  useEffect(() => {
    const bb = tg()?.BackButton;
    if (!bb) return;
    try {
      if (visible) bb.show();
      else bb.hide();
    } catch {
      /* старый клиент */
    }
  }, [visible]);
}
